import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  FileText, Building2, User as UserIcon, Home as HomeIcon, CheckCircle2, AlertCircle,
  Clock, Eye, Loader2, X,
} from 'lucide-react';
import { supabase } from '../../supabaseClient';
import { useAuth } from '../../hooks/useAuth';
import { formatDocument } from '../../utils/formatDocument';
import { callIntakeAgent } from '../../utils/callIntakeAgent';

function StatusBadge({ label, tone }) {
  const map = {
    emerald: 'bg-emerald-100 text-emerald-800',
    amber: 'bg-amber-100 text-amber-800',
    red: 'bg-red-100 text-red-800',
    blue: 'bg-blue-100 text-blue-800',
    slate: 'bg-slate-100 text-slate-700',
    orange: 'bg-orange-100 text-orange-800',
  };
  return <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${map[tone] || map.slate}`}>{label}</span>;
}

const PIPELINE_TONE = {
  planned: 'amber',
  completed: 'emerald',
  partial: 'orange',
  failed: 'red',
};

const ENTITY_ICON = {
  empresa: Building2,
  contato: UserIcon,
  imovel: HomeIcon,
};

function EntityCard({ entity }) {
  const Icon = ENTITY_ICON[entity.entity_type] || FileText;
  const data = entity.extracted_data || {};
  const nome = data.nome || data.company_name || data.empresa || data.first_name
    || (data.first_name && data.last_name ? `${data.first_name} ${data.last_name}` : null)
    || data.matricula || '(sem nome extraído)';
  const candidates = Array.isArray(entity.candidates)
    ? entity.candidates
    : (entity.candidates?.matches || []);

  let statusNode = null;
  if (entity.resolution_status === 'matched') {
    const conf = entity.resolution_confidence != null ? `${Math.round(entity.resolution_confidence * 100)}%` : '—';
    statusNode = <StatusBadge tone="emerald" label={`🟢 matched · ${entity.resolution_method || '?'} · ${conf}`} />;
  } else if (entity.resolution_status === 'queued_human') {
    statusNode = <StatusBadge tone="amber" label={`🟡 revisar (${candidates.length} candidatos)`} />;
  } else if (entity.resolution_status === 'pending') {
    statusNode = <StatusBadge tone="red" label="🔴 sem match — criar?" />;
  } else if (entity.resolution_status) {
    statusNode = <StatusBadge tone="slate" label={entity.resolution_status} />;
  }

  return (
    <div className="bg-white border border-slate-200 rounded p-2 text-xs">
      <div className="flex items-center gap-1.5 mb-1">
        <Icon size={12} className="text-slate-500 flex-shrink-0" />
        <span className="text-[10px] uppercase text-slate-500 font-semibold">{entity.entity_type}</span>
        {entity.entity_role && <span className="text-[10px] text-slate-400">· {entity.entity_role}</span>}
        <div className="ml-auto">{statusNode}</div>
      </div>
      <div className="font-medium text-slate-800 truncate" title={nome}>{nome}</div>
      {entity.cpf_cnpj && (
        <div className="text-[11px] text-slate-600 font-mono flex items-center gap-1 mt-0.5">
          {entity.entity_type === 'empresa' ? 'CNPJ' : 'CPF'}: {formatDocument(entity.cpf_cnpj)}
          {entity.cpf_cnpj_valid === true && <CheckCircle2 size={10} className="text-emerald-600" />}
          {entity.cpf_cnpj_valid === false && <AlertCircle size={10} className="text-red-600" />}
        </div>
      )}
      {entity.resolution_status === 'queued_human' && candidates.length > 0 && (
        <div className="mt-1 pl-3 border-l-2 border-amber-200 space-y-0.5">
          {candidates.slice(0, 3).map((c, idx) => {
            const cName = c.empresa || c.company_name || c.nome || c.name || c.id;
            const cConf = c.similarity != null ? `${Math.round(c.similarity * 100)}%` : c.score != null ? `${Math.round(c.score * 100)}%` : '—';
            return (
              <div key={c.id || idx} className="text-[10px] text-slate-600 flex items-center gap-1">
                <span className="truncate flex-1" title={cName}>{cName}</span>
                <span className="text-slate-400 tabular-nums">{cConf}</span>
              </div>
            );
          })}
          {candidates.length > 3 && <div className="text-[10px] text-slate-400">+{candidates.length - 3} outros</div>}
        </div>
      )}
    </div>
  );
}

function ChangesDiff({ changes }) {
  if (!changes || typeof changes !== 'object') return null;
  const entries = Object.entries(changes);
  if (entries.length === 0) return null;
  return (
    <div className="space-y-0.5 mt-1 text-[11px] font-mono">
      {entries.map(([k, v]) => {
        let from, to;
        if (v && typeof v === 'object' && ('from' in v || 'to' in v)) {
          from = v.from;
          to = v.to;
        } else {
          to = v;
        }
        const fmt = (x) => (x == null || x === '' ? <span className="text-slate-400 italic">(vazio)</span> : typeof x === 'object' ? JSON.stringify(x) : String(x));
        return (
          <div key={k} className="grid grid-cols-[100px_1fr] gap-2">
            <span className="text-slate-500">{k}:</span>
            <span>
              {from !== undefined && <>{fmt(from)} <span className="text-slate-400">→</span> </>}
              <span className="text-slate-800">{fmt(to)}</span>
            </span>
          </div>
        );
      })}
    </div>
  );
}

function ActionRow({ action, checked, onToggle, readonly }) {
  const [expanded, setExpanded] = useState(false);
  const confPct = action.confidence != null ? Math.round(action.confidence * 100) : null;

  let statusNode;
  if (action.applied) {
    statusNode = <StatusBadge tone="emerald" label="✅ aplicada" />;
  } else if (action.apply_error) {
    statusNode = <StatusBadge tone="red" label="❌ erro" />;
  } else if (action.requires_human) {
    statusNode = <StatusBadge tone="amber" label="👁 aguardando revisão" />;
  } else {
    statusNode = <StatusBadge tone="slate" label="⏳ pendente auto" />;
  }

  return (
    <div className={`bg-white border rounded p-2 text-xs ${action.applied ? 'border-emerald-200' : action.apply_error ? 'border-red-200' : 'border-slate-200'}`}>
      <div className="flex items-start gap-2">
        <input
          type="checkbox"
          checked={!!checked}
          onChange={(e) => onToggle(action.id, e.target.checked)}
          disabled={readonly || action.applied}
          className="mt-0.5"
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-slate-800">{action.action_type}</span>
            <span className="text-[10px] text-slate-500">· {action.target_table}</span>
            {!action.requires_human && <StatusBadge tone="blue" label="🏷 Auto" />}
            {action.requires_human && <StatusBadge tone="amber" label="👁 Revisar" />}
            <div className="ml-auto">{statusNode}</div>
          </div>
          {confPct != null && (
            <div className="flex items-center gap-1 mt-1 text-[10px] text-slate-500">
              <span>Confidence</span>
              <div className="w-24 h-1.5 bg-slate-200 rounded overflow-hidden">
                <div className={`h-full ${confPct >= 90 ? 'bg-emerald-500' : confPct >= 70 ? 'bg-amber-500' : 'bg-red-500'}`} style={{ width: `${confPct}%` }} />
              </div>
              <span className="tabular-nums">{confPct}%</span>
            </div>
          )}
          <ChangesDiff changes={action.changes} />
          {action.evidence_text && (
            <button
              onClick={() => setExpanded((v) => !v)}
              className="mt-1 text-[10px] text-blue-600 hover:text-blue-800 flex items-center gap-0.5"
            >
              <Eye size={9} /> {expanded ? 'ocultar evidência' : 'ver evidência'}
              {action.evidence_page && <span className="text-slate-400">· p.{action.evidence_page}</span>}
            </button>
          )}
          {expanded && action.evidence_text && (
            <div className="mt-1 p-2 bg-slate-50 border border-slate-200 rounded text-[10px] text-slate-600 whitespace-pre-wrap font-mono max-h-40 overflow-y-auto">
              {action.evidence_text}
            </div>
          )}
          {action.apply_error && (
            <div className="mt-1 text-[11px] text-red-700 bg-red-50 border border-red-200 rounded p-1.5">{action.apply_error}</div>
          )}
          {action.applied && action.applied_result_id && (
            <div className="mt-1 text-[10px] text-slate-500">result_id: <span className="font-mono">{action.applied_result_id}</span></div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * IntakeReviewPanel — revisão do diff extraído por agent-intake-document.
 *
 * Props:
 *  - intakeRunId: uuid da run
 *  - onComplete(): chamado após aprovação aplicada com sucesso
 *  - onCancel(): fecha painel sem aplicar
 *  - readonly: boolean (default false); quando true, só exibe sem checkboxes.
 */
export default function IntakeReviewPanel({ intakeRunId, onComplete, onCancel, readonly = false }) {
  const { user } = useAuth();
  const [run, setRun] = useState(null);
  const [entities, setEntities] = useState([]);
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [checked, setChecked] = useState({});
  const [applying, setApplying] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [runRes, entRes, actRes] = await Promise.all([
        supabase.from('intake_runs').select('*').eq('id', intakeRunId).single(),
        supabase.from('intake_entities').select('*').eq('run_id', intakeRunId).order('sort_order'),
        supabase.from('intake_actions').select('*').eq('run_id', intakeRunId).order('sort_order'),
      ]);
      if (runRes.error) throw runRes.error;
      if (entRes.error) throw entRes.error;
      if (actRes.error) throw actRes.error;
      setRun(runRes.data);
      setEntities(entRes.data || []);
      setActions(actRes.data || []);
      // Pré-marca: auto actions não aplicadas; não marca requires_human.
      const nextChecked = {};
      (actRes.data || []).forEach((a) => {
        if (!a.applied && !a.requires_human) nextChecked[a.id] = true;
      });
      setChecked(nextChecked);
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setLoading(false);
    }
  }, [intakeRunId]);

  useEffect(() => { if (intakeRunId) reload(); }, [intakeRunId, reload]);

  const counts = useMemo(() => {
    const autoPending = actions.filter((a) => !a.applied && !a.requires_human).length;
    const selectedPending = actions.filter((a) => checked[a.id] && !a.applied).length;
    const entMatched = entities.filter((e) => e.resolution_status === 'matched').length;
    const entQueued = entities.filter((e) => e.resolution_status === 'queued_human').length;
    return { autoPending, selectedPending, entMatched, entQueued };
  }, [actions, entities, checked]);

  const toggle = (id, v) => setChecked((prev) => ({ ...prev, [id]: v }));

  const applyIds = async (ids, label) => {
    if (ids.length === 0) return;
    setApplying(true);
    try {
      const result = await callIntakeAgent({
        mode: 'apply',
        run_id: intakeRunId,
        action_ids: ids,
        applied_by: user?.email || user?.id,
      });
      if (!result.ok) {
        // eslint-disable-next-line no-alert
        alert(`Falha ao aplicar ${label}: ${result.error || 'erro desconhecido'}`);
      }
      await reload();
      if (result.ok && onComplete) onComplete();
    } catch (err) {
      // eslint-disable-next-line no-alert
      alert(`Erro: ${err.message}`);
    } finally {
      setApplying(false);
    }
  };

  const handleApproveAuto = () => {
    const ids = actions.filter((a) => !a.applied && !a.requires_human).map((a) => a.id);
    applyIds(ids, 'ações automáticas');
  };

  const handleApproveSelected = () => {
    const ids = actions.filter((a) => checked[a.id] && !a.applied).map((a) => a.id);
    applyIds(ids, 'ações selecionadas');
  };

  if (!intakeRunId) return null;

  return (
    <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-3">
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center flex-wrap gap-2 text-xs">
          <FileText size={14} className="text-blue-600" />
          <span className="font-semibold text-slate-800">Intake</span>
          {run?.document_type && <StatusBadge tone="blue" label={run.document_type} />}
          {run?.pipeline_status && (
            <StatusBadge tone={PIPELINE_TONE[run.pipeline_status] || 'slate'} label={run.pipeline_status} />
          )}
          {!loading && (
            <span className="text-[11px] text-slate-500">
              {entities.length} extraídas · {counts.entMatched} matched · {counts.entQueued} revisão
            </span>
          )}
        </div>
        {onCancel && (
          <button onClick={onCancel} className="text-slate-400 hover:text-slate-700" title="Fechar">
            <X size={14} />
          </button>
        )}
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-xs text-slate-500 py-3">
          <Loader2 size={14} className="animate-spin" /> Carregando revisão...
        </div>
      )}
      {!loading && error && (
        <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2">{error}</div>
      )}

      {!loading && !error && (
        <>
          {entities.length > 0 && (
            <section>
              <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1.5">
                Entidades extraídas ({entities.length})
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {entities.map((e) => <EntityCard key={e.id} entity={e} />)}
              </div>
            </section>
          )}

          <section>
            <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1.5">
              Plano de ações ({actions.length})
            </div>
            {actions.length === 0 ? (
              <div className="text-xs text-slate-400 italic">Nenhuma ação sugerida.</div>
            ) : (
              <div className="space-y-1.5">
                {actions.map((a) => (
                  <ActionRow
                    key={a.id}
                    action={a}
                    checked={checked[a.id]}
                    onToggle={toggle}
                    readonly={readonly}
                  />
                ))}
              </div>
            )}
          </section>

          {/* Footer */}
          {!readonly && (
            <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-200">
              <button
                onClick={onCancel}
                disabled={applying}
                className="px-3 py-1.5 text-xs text-slate-600 hover:bg-slate-100 rounded border border-slate-200"
              >
                Cancelar
              </button>
              <button
                onClick={handleApproveAuto}
                disabled={applying || counts.autoPending === 0}
                className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1"
                title="Aplica apenas ações com requires_human=false"
              >
                {applying ? <Loader2 size={12} className="animate-spin" /> : <Clock size={12} />}
                Aprovar automáticas ({counts.autoPending})
              </button>
              <button
                onClick={handleApproveSelected}
                disabled={applying || counts.selectedPending === 0}
                className="px-3 py-1.5 text-xs bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-1"
                title="Aplica apenas as ações marcadas (incluindo as de revisão humana)"
              >
                {applying ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />}
                Aprovar selecionadas ({counts.selectedPending})
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
